import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Sun, Moon } from 'lucide-react';

const ThemeToggle: React.FC = () => {
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    // Check for saved theme preference or default to light mode
    const savedTheme = localStorage.getItem('theme');
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    
    if (savedTheme === 'dark' || (!savedTheme && prefersDark)) {
      setIsDark(true);
      document.documentElement.classList.add('dark');
    } else {
      setIsDark(false);
      document.documentElement.classList.remove('dark');
    }
  }, []);

  const toggleTheme = () => {
    const newTheme = !isDark;
    setIsDark(newTheme);
    
    if (newTheme) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  };

  return (
    <motion.button
      onClick={toggleTheme}
      whileHover={{ scale: 1.04, y: -1 }}
      whileTap={{ scale: 0.9 }}
      className="relative flex h-11 w-11 items-center justify-center overflow-hidden rounded-full border border-white/10 bg-white/5 text-slate-100 shadow-cosmic-glass backdrop-blur-xl transition-all duration-300 hover:border-cosmic-aurora-emerald/50 hover:shadow-aurora-emerald"
      initial={{ opacity: 0, scale: 0 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: 0.5, duration: 0.5 }}
      aria-label="Toggle theme"
    >
      <motion.div
        className="absolute inset-0 rounded-full bg-aurora-borealis opacity-10"
        animate={{ rotate: isDark ? 0 : 180 }}
        transition={{ duration: 0.5 }}
      />
      
      <motion.div
        animate={{ rotate: isDark ? 0 : 180 }}
        transition={{ duration: 0.5 }}
        className="relative z-10"
      >
        {isDark ? (
          <Sun 
            size={20} 
            className="text-cosmic-aurora-gold transition-colors duration-300" 
          />
        ) : (
          <Moon 
            size={20} 
            className="text-cosmic-aurora-sapphire transition-colors duration-300" 
          />
        )}
      </motion.div>
      
      <motion.div
        className="absolute inset-0 rounded-full"
        animate={{
          boxShadow: isDark 
            ? '0 0 18px rgba(245, 185, 66, 0.2), 0 0 34px rgba(245, 185, 66, 0.08)' 
            : '0 0 18px rgba(96, 165, 250, 0.2), 0 0 34px rgba(96, 165, 250, 0.08)'
        }}
        transition={{ duration: 0.5 }}
      />
    </motion.button>
  );
};

export default ThemeToggle;
